import React, { useState, useEffect, useMemo } from 'react';
import { TestResult, Scenario, Project } from '../types';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import EditProjectModal from '../components/EditProjectModal';
import { HomeTestCard } from '../components/HomeTestCard';
import { useToast } from '../contexts/ToastContext';
import { generateUniqueId } from '../utils/scenarioUtils';

interface HomeScreenProps {
  projects: Project[];
  selectedProject: Project | null;
  selectedFile: File | null;
  testResults: TestResult[];
  scenarios: Scenario[];
  onProjectSelect: (project: Project | null) => void;
  onFileSelect: (file: File) => void;
  onNewTestClick: () => void;
  onContinueTest: (result: TestResult) => void;
  onProjectsUpdate?: (projects: Project[]) => void;
  username: string;
  onAnalyzeProject?: (project: Project) => void;
  onNavigate?: (screen: string, testId?: string, projectId?: string) => void;
  onEditCSV?: () => void;
}

const HomeScreen: React.FC<HomeScreenProps> = ({
  projects = [],
  selectedProject,
  selectedFile,
  testResults,
  scenarios,
  onProjectSelect,
  onFileSelect,
  onNewTestClick,
  onContinueTest,
  onProjectsUpdate,
  username,
  onAnalyzeProject,
  onNavigate,
  onEditCSV
}) => {
  const { showToast } = useToast();
  const [selectedVersion, setSelectedVersion] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [fileToDelete, setFileToDelete] = useState<{ id: string; name: string } | null>(null);
  const [isAddingProject, setIsAddingProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set());
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null);
  const [isCustomNameModalOpen, setIsCustomNameModalOpen] = useState(false);
  const [fileToUpload, setFileToUpload] = useState<File | null>(null);
  const [customFileName, setCustomFileName] = useState('');
  
  const API_BASE_URL = process.env.REACT_APP_API_HOST;

  // Funkcja grupująca testy według wersji
  const getVersionStats = () => {
    if (!selectedFile) return [];
    
    const filteredResults = testResults.filter(result => result.filename === selectedFile.name);
    
    // Grupuj testy według wersji
    const versionGroups: {[key: string]: TestResult[]} = {};
    
    filteredResults.forEach(result => {
      const version = result.metadata?.version || 'Nieokreślona';
      if (!versionGroups[version]) {
        versionGroups[version] = [];
      }
      versionGroups[version].push(result);
    });
    
    // Przekształć grupy na tablicę statystyk
    return Object.entries(versionGroups).map(([version, results]) => {
      // Oblicz statystyki na podstawie scenarioResults z uwzględnieniem wybranych kategorii
      const totalScenarios = results.reduce((sum, result) => {
        const scenarioResults = result.scenarioResults || {};
        
        // Jeśli test ma wybrane kategorie i nie jest pełną regresją, filtruj scenariusze
        if (result.metadata.selectedCategories && result.metadata.selectedCategories.length > 0 && !result.metadata.isFullRegression) {
          // Musimy pobrać informacje o kategoriach scenariuszy z scenarios
          const scenarioCategories = scenarios.reduce((acc, scenario) => {
            acc[scenario['ID testu']] = scenario.Kategoria;
            return acc;
          }, {} as Record<string, string>);
          
          // Licz tylko scenariusze z wybranych kategorii
          const filteredCount = Object.keys(scenarioResults).filter(scenarioId => {
            const category = scenarioCategories[scenarioId];
            return category && result.metadata.selectedCategories && result.metadata.selectedCategories.includes(category);
          }).length;
          
          return sum + filteredCount;
        }
        
        return sum + Object.keys(scenarioResults).length;
      }, 0);
      
      const totalPassed = results.reduce((sum, result) => {
        const scenarioResults = result.scenarioResults || {};
        
        // Jeśli test ma wybrane kategorie i nie jest pełną regresją, filtruj scenariusze
        if (result.metadata.selectedCategories && result.metadata.selectedCategories.length > 0 && !result.metadata.isFullRegression) {
          const scenarioCategories = scenarios.reduce((acc, scenario) => {
            acc[scenario['ID testu']] = scenario.Kategoria;
            return acc;
          }, {} as Record<string, string>);
          
          // Licz tylko zaliczone scenariusze z wybranych kategorii
          const filteredPassed = Object.entries(scenarioResults).filter(([scenarioId, scenarioResult]) => {
            const category = scenarioCategories[scenarioId];
            return category && result.metadata.selectedCategories && result.metadata.selectedCategories.includes(category) && scenarioResult.Status === 'Passed';
          }).length;
          
          return sum + filteredPassed;
        }
        
        return sum + Object.values(scenarioResults).filter(r => r.Status === 'Passed').length;
      }, 0);
      
      const totalFailed = results.reduce((sum, result) => {
        const scenarioResults = result.scenarioResults || {};
        
        // Jeśli test ma wybrane kategorie i nie jest pełną regresją, filtruj scenariusze
        if (result.metadata.selectedCategories && result.metadata.selectedCategories.length > 0 && !result.metadata.isFullRegression) {
          const scenarioCategories = scenarios.reduce((acc, scenario) => {
            acc[scenario['ID testu']] = scenario.Kategoria;
            return acc;
          }, {} as Record<string, string>);
          
          // Licz tylko niezaliczone scenariusze z wybranych kategorii
          const filteredFailed = Object.entries(scenarioResults).filter(([scenarioId, scenarioResult]) => {
            const category = scenarioCategories[scenarioId];
            return category && result.metadata.selectedCategories && result.metadata.selectedCategories.includes(category) && scenarioResult.Status === 'Failed';
          }).length;
          
          return sum + filteredFailed;
        }
        
        return sum + Object.values(scenarioResults).filter(r => r.Status === 'Failed').length;
      }, 0);
      
      const totalBlocked = results.reduce((sum, result) => {
        const scenarioResults = result.scenarioResults || {};
        
        // Jeśli test ma wybrane kategorie i nie jest pełną regresją, filtruj scenariusze
        if (result.metadata.selectedCategories && result.metadata.selectedCategories.length > 0 && !result.metadata.isFullRegression) {
          const scenarioCategories = scenarios.reduce((acc, scenario) => {
            acc[scenario['ID testu']] = scenario.Kategoria;
            return acc;
          }, {} as Record<string, string>);
          
          // Licz tylko zablokowane scenariusze z wybranych kategorii
          const filteredBlocked = Object.entries(scenarioResults).filter(([scenarioId, scenarioResult]) => {
            const category = scenarioCategories[scenarioId];
            return category && result.metadata.selectedCategories && result.metadata.selectedCategories.includes(category) && scenarioResult.Status === 'Blocked';
          }).length;
          
          return sum + filteredBlocked;
        }
        
        return sum + Object.values(scenarioResults).filter(r => r.Status === 'Blocked').length;
      }, 0);
      
      return {
        version,
        count: results.length,
        total: totalScenarios,
        passed: totalPassed,
        failed: totalFailed,
        blocked: totalBlocked
      };
    }).sort((a, b) => {
      if (a.version === 'Nieokreślona') return 1;
      if (b.version === 'Nieokreślona') return -1;
      return b.version.localeCompare(a.version);
    });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- getVersionStats depends only on selectedFile, testResults, scenarios
  const versionStats = useMemo(() => getVersionStats(), [selectedFile, testResults, scenarios]);

  // Filtruj testy według wybranej wersji
  const getFilteredTestResults = () => {
    if (!selectedFile) return [];
    
    let results = testResults.filter(result => result.filename === selectedFile.name);
    
    if (selectedVersion) {
      results = results.filter(result => {
        const version = result.metadata?.version || 'Nieokreślona';
        return version === selectedVersion;
      });
    }
    
    return results.sort((a, b) =>
      new Date(b.metadata.startDate).getTime() - new Date(a.metadata.startDate).getTime()
    );
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- getFilteredTestResults depends only on selectedFile, testResults, selectedVersion
  const filteredResults = useMemo(() => getFilteredTestResults(), [selectedFile, testResults, selectedVersion]);

  // Obsługa kliknięcia na wersję
  const handleVersionClick = (version: string) => {
    if (selectedVersion === version) {
      // Kliknięcie na już wybraną wersję wyłącza filtr
      setSelectedVersion(null);
    } else {
      setSelectedVersion(version);
    }
  };

  const handleFileUpload = async (file: File) => {
    if (!selectedProject) {
      showToast('Najpierw wybierz projekt', 'error');
      return;
    }

    setFileToUpload(file);
    setCustomFileName(file.name.split('.')[0]); // Domyślna nazwa bez rozszerzenia
    setIsCustomNameModalOpen(true);
  };

  const handleCustomNameSubmit = async () => {
    if (!fileToUpload || !selectedProject) return;

    const formData = new FormData();
    formData.append('file', fileToUpload);
    formData.append('projectId', selectedProject.id);
    formData.append('fileId', generateUniqueId());
    formData.append('customName', customFileName);

    try {
      const response = await fetch(`${API_BASE_URL}/api/upload-csv`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Błąd podczas przesyłania pliku');
      }

      const data = await response.json();
      showToast('Plik został pomyślnie przesłany', 'success');

      // Odśwież listę projektów
      if (onProjectsUpdate) {
        const updatedProjects = projects.map(project => {
          if (project.id === selectedProject.id) {
            return {
              ...project,
              csvFiles: [...project.csvFiles, {
                id: data.fileId,
                name: data.customName,
                originalName: data.filename,
                testIds: []
              }]
            };
          }
          return project;
        });
        onProjectsUpdate(updatedProjects);
      }

      // Wybierz nowo dodany plik
      const newFile = new File([], data.filename);
      onFileSelect(newFile);

      // Zamknij modal i wyczyść stan
      setIsCustomNameModalOpen(false);
      setFileToUpload(null);
      setCustomFileName('');
    } catch (error) {
      showToast('Wystąpił błąd podczas przesyłania pliku', 'error');
    }
  };

  const handleAddProject = async () => {
    if (!newProjectName.trim()) {
      setUploadError('Nazwa projektu jest wymagana');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setUploadError('Brak tokenu autoryzacji. Zaloguj się ponownie.');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/projects`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: newProjectName,
          description: '',
          csvFiles: [],
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}: ${response.statusText}` }));
        throw new Error(errorData.error || `Błąd podczas tworzenia projektu (${response.status})`);
      }

      await response.json();

      // Pobierz zaktualizowaną listę projektów
      const projectsResponse = await fetch(`${API_BASE_URL}/api/projects`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (projectsResponse.ok) {
        const updatedProjects = await projectsResponse.json();
        if (onProjectsUpdate) {
          onProjectsUpdate(updatedProjects);
        }
      }

      setNewProjectName('');
      setIsAddingProject(false);
      setUploadError(null);
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : 'Wystąpił nieznany błąd');
    }
  };

  const handleEditProject = async (project: Project) => {
    setEditingProject(project);
    setIsEditModalOpen(true);
  };

  const handleDeleteProject = async (projectId: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Błąd podczas usuwania projektu');
      }

      // Odświeżamy listę projektów
      const projectsResponse = await fetch(`${API_BASE_URL}/api/projects`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (projectsResponse.ok) {
        const updatedProjects = await projectsResponse.json();
        onProjectsUpdate?.(updatedProjects);
      }

      // Jeśli usunięty projekt był wybrany, wyczyść wybór
      if (selectedProject?.id === projectId) {
        onProjectSelect(null);
      }

      setProjectToDelete(null);
      showToast('Projekt został pomyślnie usunięty', 'success');
    } catch (error) {
      showToast('Wystąpił błąd podczas usuwania projektu', 'error');
    }
  };

  const handleUpdateProject = async (updatedProject: Project) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/projects/${updatedProject.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updatedProject),
      });

      if (!response.ok) {
        throw new Error('Błąd podczas aktualizacji projektu');
      }

      // Odświeżamy listę projektów
      const projectsResponse = await fetch(`${API_BASE_URL}/api/projects`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (projectsResponse.ok) {
        const updatedProjects = await projectsResponse.json();
        onProjectsUpdate?.(updatedProjects);
      }

      setIsEditModalOpen(false);
      setEditingProject(null);
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : 'Wystąpił nieznany błąd');
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragging(false);
    
    const file = event.dataTransfer.files?.[0];
    if (file && (file.type === 'text/csv' || file.name.endsWith('.csv'))) {
      await handleFileUpload(file);
    } else {
      setUploadError('Proszę upuścić plik CSV');
    }
  };

  const handleDeleteFile = (file: { id: string; name: string }) => {
    setFileToDelete(file);
  };

  const handleConfirmDelete = async () => {
    if (!fileToDelete || !selectedProject) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/projects/${selectedProject.id}/csv-files/${fileToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Nie udało się usunąć pliku');
      }

      // Odśwież listę projektów
      const projectsResponse = await fetch(`${API_BASE_URL}/api/projects`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const updatedProjects = await projectsResponse.json();
      if (onProjectsUpdate) {
        onProjectsUpdate(updatedProjects);
      }

      // Jeśli usunięty plik był aktualnie wybrany, wyczyść wybór
      if (selectedFile && selectedFile.name === fileToDelete.name) {
        onFileSelect(new File([], ''));
      }

      setFileToDelete(null);
      showToast('Plik został pomyślnie usunięty', 'success');
    } catch (error) {
      showToast('Wystąpił błąd podczas usuwania pliku', 'error');
    }
  };

  const handleDropdownClick = (e: React.MouseEvent, projectId: string) => {
    e.stopPropagation();
    setOpenDropdownId(openDropdownId === projectId ? null : projectId);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openDropdownId) {
        setOpenDropdownId(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [openDropdownId]);

  const handleProjectClick = (project: Project) => {
    setExpandedProjects(prev => {
      const newSet = new Set(prev);
      if (newSet.has(project.id)) {
        newSet.delete(project.id);
      } else {
        newSet.add(project.id);
      }
      return newSet;
    });
    onProjectSelect(project);
  };

  // Reserved for navbar/quick-access "last test" link
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleLastTestClick = () => {
    const lastTestId = localStorage.getItem('lastTestId');
    if (lastTestId && selectedProject) {
      onNavigate?.('test', lastTestId, selectedProject.id);
    } else if (lastTestId) {
      onNavigate?.('test', lastTestId);
    }
  };

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-12 pt-24 home-screen" id="home-screen">
      <div className="w-full max-w-[2000px] mx-auto bg-white/60 dark:bg-slate-800/90 backdrop-blur-sm rounded-2xl shadow-lg p-8 home-screen__container">
        <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2 home-screen__title" id="home-screen-title">Test Manager</h1>
        <p className="text-lg text-gray-600 dark:text-gray-300 mb-12 home-screen__subtitle" id="home-screen-subtitle">System zarządzania testami aplikacji</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 home-screen__content">
          <div className="bg-white dark:bg-slate-700/80 rounded-2xl shadow-xl p-8 transform transition-all duration-200 hover:shadow-2xl projects-section" id="projects-section">
            <div className="flex justify-between items-center mb-6 projects-section__header">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white flex items-center projects-section__title" id="projects-title">
                <svg className="w-7 h-7 mr-2 text-blue-500 projects-section__icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
                Projekty
              </h2>
              <button
                onClick={() => setIsAddingProject(true)}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors projects-section__add-button"
                id="add-project-button"
              >
                Dodaj projekt
              </button>
            </div>

            {isAddingProject && (
              <div className="mb-6 p-4 bg-gray-50 dark:bg-slate-600/50 rounded-lg projects-section__add-form" id="add-project-form">
                <input
                  type="text"
                  value={newProjectName}
                  onChange={(e) => {
                    setNewProjectName(e.target.value);
                    setUploadError(null);
                  }}
                  placeholder="Nazwa projektu"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-slate-500 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-slate-600 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 projects-section__input"
                  id="new-project-name-input"
                />
                {uploadError && (
                  <p className="text-red-600 dark:text-red-400 text-sm mt-1" role="alert">{uploadError}</p>
                )}
                <div className="flex justify-end gap-2 mt-2 projects-section__form-actions">
                  <button
                    onClick={() => {
                      setIsAddingProject(false);
                      setNewProjectName('');
                      setUploadError(null);
                    }}
                    className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white projects-section__cancel-button"
                    id="cancel-add-project-button"
                  >
                    Anuluj
                  </button>
                  <button
                    onClick={handleAddProject}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 projects-section__save-button"
                    id="save-project-button"
                  >
                    Zapisz
                  </button>
                </div>
              </div>
            )}

            <div className="space-y-4 projects-section__list" id="projects-list">
              {projects.map((project) => (
                <div
                  key={project.id}
                  className={`border rounded-xl projects-section__project ${
                    selectedProject?.id === project.id
                      ? 'projects-section__project--selected border-blue-500 shadow-md'
                      : 'border-gray-200'
                  }`}
                  id={`project-${project.id}`}
                >
                  <div
                    onClick={() => handleProjectClick(project)}
                    className="p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-800/80 relative projects-section__project-header rounded-xl"
                    id={`project-header-${project.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <svg className="w-6 h-6 text-blue-500 projects-section__project-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                        </svg>
                        <div>
                          <h3 className="text-lg font-medium text-gray-900 dark:text-white projects-section__project-name">{project.name}</h3>
                          {project.description && (
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 projects-section__project-description">{project.description}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onAnalyzeProject?.(project);
                          }}
                          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2 projects-section__analyze-button"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                          Analizuj
                        </button>
                        <svg 
                          className={`w-5 h-5 text-gray-400 transition-transform duration-200 projects-section__project-arrow ${
                            expandedProjects.has(project.id) ? 'projects-section__project-arrow--expanded rotate-180' : ''
                          }`} 
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                  
                  {expandedProjects.has(project.id) && (
                    <div className="border-t border-gray-200 dark:border-slate-600 p-4 bg-gray-50 dark:bg-slate-600/40 projects-section__project-content" id={`project-content-${project.id}`}>
                      <div className="flex justify-between items-center mb-4">
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-200 projects-section__files-title" id={`project-files-title-${project.id}`}>Pliki CSV</h4>
                        <div className="relative">
                          <button
                            onClick={(e) => handleDropdownClick(e, project.id)}
                            className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 focus:outline-none projects-section__project-menu-button"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                            </svg>
                          </button>
                          {openDropdownId === project.id && (
                            <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-slate-700 rounded-md shadow-lg z-[100] border border-gray-200 dark:border-slate-600 projects-section__project-menu">
                              <div className="py-1">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditProject(project);
                                    setOpenDropdownId(null);
                                  }}
                                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-600 projects-section__project-menu-item"
                                >
                                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                  Edytuj
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setProjectToDelete(project);
                                    setOpenDropdownId(null);
                                  }}
                                  className="flex items-center w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-slate-600 projects-section__project-menu-item projects-section__project-menu-item--delete"
                                >
                                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                  Usuń
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="space-y-2 projects-section__files-list" id={`project-files-list-${project.id}`}>
                        {project.csvFiles.map((file) => (
                          <div
                            key={file.id}
                            onClick={() => {
                              const newFile = new File([], file.originalName);
                              onFileSelect(newFile);
                            }}
                            className={`flex items-center justify-between p-3 bg-white dark:bg-slate-600 rounded-lg border transition-colors projects-section__file cursor-pointer ${
                              selectedFile && selectedFile.name === file.originalName
                                ? 'projects-section__file--selected border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/30'
                                : 'border-gray-200 dark:border-slate-500 hover:border-blue-500 dark:hover:border-blue-400'
                            }`}
                            id={`file-${file.id}`}
                          >
                            <div className="flex items-center space-x-3">
                              <svg
                                className={`w-5 h-5 projects-section__file-icon ${
                                  selectedFile && selectedFile.name === file.originalName
                                    ? 'text-blue-500'
                                    : 'text-gray-400'
                                }`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                />
                              </svg>
                              <span className={`projects-section__file-name ${
                                selectedFile && selectedFile.name === file.originalName
                                  ? 'text-blue-700 dark:text-blue-300 font-medium'
                                  : 'text-gray-900 dark:text-gray-100'
                              }`}>{file.name}</span>
                            </div>
                            <div className="flex items-center space-x-2 projects-section__file-actions">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const newFile = new File([], file.originalName);
                                  onFileSelect(newFile);
                                }}
                                className={`p-1 projects-section__file-action ${
                                  selectedFile && selectedFile.name === file.originalName
                                    ? 'text-blue-600 hover:text-blue-700'
                                    : 'text-blue-500 hover:text-blue-600'
                                }`}
                                title="Wybierz plik"
                              >
                                <svg
                                  className="w-5 h-5"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                  />
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                  />
                                </svg>
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteFile(file);
                                }}
                                className="p-1 text-red-500 hover:text-red-600 projects-section__file-action projects-section__file-action--delete"
                                title="Usuń plik"
                              >
                                <svg
                                  className="w-5 h-5"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                  />
                                </svg>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Sekcja drag and drop */}
                      <div
                        className={`mt-8 p-6 border-2 border-dashed rounded-lg text-center projects-section__drop-zone ${
                          isDragging ? 'projects-section__drop-zone--active border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/30' : 'border-gray-300 dark:border-slate-500'
                        }`}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        id={`drop-zone-${project.id}`}
                      >
                        <div className="flex flex-col items-center space-y-4">
                          <div className="relative">
                            <svg
                              className="w-12 h-12 text-blue-500 projects-section__drop-zone-icon"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                              />
                            </svg>
                            <svg
                              className="w-6 h-6 text-blue-500 absolute -bottom-2 -right-2 projects-section__drop-zone-icon-plus"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                              />
                            </svg>
                          </div>
                          <div className="space-y-2">
                            <p className="text-lg font-medium text-gray-700 dark:text-gray-200 projects-section__drop-zone-text">
                              Przeciągnij i upuść plik CSV
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400 projects-section__drop-zone-subtext">
                              lub kliknij, aby wybrać plik
                            </p>
                          </div>
                          <input
                            type="file"
                            accept=".csv"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                handleFileUpload(file);
                              }
                            }}
                            className="hidden"
                            id="file-upload"
                          />
                          <label
                            htmlFor="file-upload"
                            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors cursor-pointer projects-section__drop-zone-button"
                          >
                            Wybierz plik
                          </label>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white dark:bg-slate-700/80 rounded-2xl shadow-xl p-8 transform transition-all duration-200 hover:shadow-2xl test-options-section" id="test-options-section">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center test-options-section__title" id="test-options-title">
              <svg className="w-7 h-7 mr-2 text-blue-500 test-options-section__icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
              </svg>
              Opcje testowania
            </h2>
            {selectedFile ? (
              <div className="space-y-6 test-options-section__content" id="test-options-content">
                <button
                  onClick={onNewTestClick}
                  className="w-full px-6 py-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-md hover:shadow-xl flex items-center justify-center text-lg font-medium test-options-section__new-test-button"
                  id="new-test-button"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Rozpocznij nowy test
                </button>
                {onEditCSV && (
                  <button
                    onClick={onEditCSV}
                    className="w-full px-6 py-3 bg-white dark:bg-slate-600 border-2 border-gray-200 dark:border-slate-500 text-gray-700 dark:text-gray-200 rounded-xl hover:border-blue-500 dark:hover:border-blue-400 hover:text-blue-600 dark:hover:text-blue-400 transition-all duration-200 flex items-center justify-center font-medium"
                    title="Edycja zawartości pliku CSV ze scenariuszami"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Edytuj plik CSV
                  </button>
                )}
                <div className="mt-8">
                  <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-6 flex items-center">
                    <svg className="w-6 h-6 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Poprzednie testy
                  </h3>

                  {/* Statystyki według wersji */}
                  {versionStats.length > 0 && (
                    <div className="bg-gray-50 dark:bg-slate-600/50 rounded-xl p-4 mb-6">
                      <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3 flex items-center">
                        <svg className="w-4 h-4 mr-1 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                        Statystyki według wersji
                      </h4>
                      <div className="space-y-3">
                        {versionStats.map((stat) => (
                          <div key={stat.version} className="flex items-center justify-between py-2 px-3 bg-white dark:bg-slate-600 rounded-lg shadow-sm">
                            <div className="flex items-center">
                              <div 
                                className={`flex items-center px-3 py-1 rounded-full mr-3 cursor-pointer transition-colors ${
                                  selectedVersion === stat.version 
                                    ? 'bg-blue-600 text-white' 
                                    : 'bg-blue-50 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/60'
                                }`}
                                onClick={() => handleVersionClick(stat.version)}
                              >
                                <span className="text-xs font-medium">
                                  {stat.version === 'Nieokreślona' ? 'Nieokreślona' : `v${stat.version}`}
                                </span>
                              </div>
                              <span className="text-sm text-gray-500 dark:text-gray-400">{stat.count} {stat.count === 1 ? 'test' : 'testy'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="flex items-center bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-1 rounded-full">
                                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                <span className="text-xs">{stat.passed}</span>
                              </div>
                              <div className="flex items-center bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 px-2 py-1 rounded-full">
                                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                                <span className="text-xs">{stat.failed}</span>
                              </div>
                              <div className="flex items-center bg-yellow-50 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 px-2 py-1 rounded-full">
                                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                </svg>
                                <span className="text-xs">{stat.blocked}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Wyświetlenie informacji o aktywnym filtrze wersji */}
                  {selectedVersion && (
                    <div className="flex justify-between items-center bg-blue-50 dark:bg-blue-900/30 rounded-lg px-4 py-2 mb-4">
                      <div className="flex items-center">
                        <svg className="w-4 h-4 text-blue-600 dark:text-blue-400 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                        </svg>
                        <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
                          Filtr: {selectedVersion === 'Nieokreślona' ? 'Wersja nieokreślona' : `Wersja ${selectedVersion}`}
                        </span>
                      </div>
                      <button 
                        className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 bg-white dark:bg-slate-600 px-2 py-1 rounded-md transition-colors"
                        onClick={() => setSelectedVersion(null)}
                      >
                        Wyczyść filtr
                      </button>
                    </div>
                  )}
                  
                  <div className="space-y-4">
                    {filteredResults.map(result => (
                      <HomeTestCard
                        key={result.metadata?.id || generateUniqueId()}
                        result={result}
                        scenarios={scenarios}
                        username={username}
                        onContinue={() => onContinueTest(result)}
                      />
                    ))}
                    
                    {/* Komunikat, gdy brak testów po zastosowaniu filtra */}
                    {selectedFile && filteredResults.length === 0 && (
                      <div className="bg-gray-50 dark:bg-slate-600/50 rounded-xl p-8 text-center">
                        {selectedVersion ? (
                          <div>
                            <svg className="w-12 h-12 mx-auto text-gray-400 dark:text-gray-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                            </svg>
                            <p className="text-gray-600 dark:text-gray-300 mb-2">Brak testów dla wybranej wersji</p>
                            <button 
                              className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium"
                              onClick={() => setSelectedVersion(null)}
                            >
                              Wyczyść filtr wersji
                            </button>
                          </div>
                        ) : (
                          <div>
                            <svg className="w-12 h-12 mx-auto text-gray-400 dark:text-gray-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <p className="text-gray-600 dark:text-gray-300">Brak poprzednich testów dla tego pliku CSV</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 test-options-section__empty-state" id="no-file-selected-message">
                <svg className="w-16 h-16 mx-auto text-gray-400 dark:text-gray-500 mb-4 test-options-section__empty-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <p className="text-gray-500 dark:text-gray-400 text-lg test-options-section__empty-text">Wybierz plik CSV, aby zobaczyć opcje testowania</p>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Modal potwierdzenia usunięcia projektu */}
      {projectToDelete && (
        <DeleteConfirmationModal
          isOpen={!!projectToDelete}
          onClose={() => setProjectToDelete(null)}
          onConfirm={() => handleDeleteProject(projectToDelete.id)}
          title="Usuń projekt"
          message={`Czy na pewno chcesz usunąć projekt "${projectToDelete.name}"? Tej operacji nie można cofnąć.`}
        />
      )}

      {/* Modal potwierdzenia usunięcia pliku */}
      {fileToDelete && (
        <DeleteConfirmationModal
          isOpen={!!fileToDelete}
          onClose={() => setFileToDelete(null)}
          onConfirm={handleConfirmDelete}
          title="Usuń plik CSV"
          message={`Czy na pewno chcesz usunąć plik "${fileToDelete.name}"? Tej operacji nie można cofnąć.`}
        />
      )}

      {/* Edit Project Modal */}
      {editingProject && (
        <EditProjectModal
          project={editingProject}
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setEditingProject(null);
          }}
          onSave={handleUpdateProject}
        />
      )}

      {/* Modal do wprowadzania własnej nazwy pliku */}
      {isCustomNameModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50 modal modal--custom-name" id="custom-name-modal">
          <div className="bg-white dark:bg-slate-800 rounded-lg p-6 w-96 modal__content">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 modal__title" id="custom-name-modal-title">
              Wprowadź nazwę pliku
            </h3>
            <input
              type="text"
              value={customFileName}
              onChange={(e) => setCustomFileName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 modal__input"
              placeholder="Nazwa pliku"
              id="custom-file-name-input"
            />
            <div className="mt-4 flex justify-end space-x-3 modal__actions">
              <button
                onClick={() => {
                  setIsCustomNameModalOpen(false);
                  setFileToUpload(null);
                  setCustomFileName('');
                }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white modal__button modal__button--cancel"
                id="cancel-custom-name-button"
              >
                Anuluj
              </button>
              <button
                onClick={handleCustomNameSubmit}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 modal__button modal__button--confirm"
                id="save-custom-name-button"
              >
                Zapisz
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomeScreen; 